import { Paper, Skeleton, styled } from '@mui/material';

import { FALLBACK_ICON_TEST_ID } from '../../global.constants';
import { ChkImage } from '../ChkImage/ChkImage';
import { ChkIconBankPlaceholder } from '../icons';

import type {
  ChkLogoContentProps,
  ChkLogoWrapperProps,
} from './ChkLogoWrapper.types';

export const ChkLogoWrapper = ({
  elevation = 0,
  isLoading,
  iconUrl,
  ...rest
}: ChkLogoWrapperProps) => {
  return (
    <StyledPaper
      iconUrl={iconUrl}
      elevation={elevation}
      role="presentation"
      {...rest}
    >
      <ChkLogoContent iconUrl={iconUrl} isLoading={isLoading} />
    </StyledPaper>
  );
};

const ChkLogoContent = ({ iconUrl, isLoading }: ChkLogoContentProps) => {
  if (isLoading) {
    return <Skeleton variant="rectangular" width="100%" height="100%" />;
  }

  if (!iconUrl) {
    return (
      <ChkIconBankPlaceholder size="lg" data-testid={FALLBACK_ICON_TEST_ID} />
    );
  }

  return (
    <ChkImage
      loadingComponent={
        <Skeleton variant="rectangular" width="100%" height="100%" />
      }
      errorComponent={
        <ErrorWrapper>
          <ChkIconBankPlaceholder
            size="lg"
            data-testid={FALLBACK_ICON_TEST_ID}
          />
        </ErrorWrapper>
      }
      src={iconUrl}
      alt=""
    />
  );
};

const ErrorWrapper = styled('span')(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.tokens.iconHoldingIconBackground,
}));

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'iconUrl',
})<{ iconUrl: ChkLogoWrapperProps['iconUrl'] }>(({ theme, iconUrl }) => ({
  width: 'min(5.6rem,100%)',
  aspectRatio: '1 / 1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '20%',
  overflow: 'hidden',
  backgroundColor: iconUrl
    ? 'transparent'
    : theme.tokens.iconHoldingIconBackground,
  filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.08))',
  boxShadow:
    'inset 0 0 2px rgba(0, 0, 0, 0.05), inset 0 0 6px rgba(0, 0, 0, 0.03)',

  img: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  alignContent: 'center',
}));
